/**
 * Route guards.
 *
 * These are **convenience, not security.** They stop someone typing `/admin/users`
 * into the address bar and seeing a broken shell; they do not protect data. Every
 * request the screen makes is authorised again by `iam.can()` in the handler and
 * filtered again by PostgreSQL row-level security. A bug here shows an empty page,
 * never someone else's records.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { PageLoader, EmptyState } from '../components/common/index.jsx';

/**
 * Require a signed-in session.
 *
 * While the boot check is still running it renders a loader rather than
 * redirecting. Redirecting on `unknown` would bounce every user with a valid
 * stored token to the login screen for the moment it takes `/auth/me` to answer.
 *
 * `PageLoader` waits 180ms before drawing anything, so a warm `/auth/me` goes
 * straight from the boot splash to the dashboard with no loader flashing in
 * between — three states in 200ms reads as a fault, not as progress.
 */
export function RequireAuth({ children }) {
  const { isAuthenticated, isResolving } = useAuth();
  const location = useLocation();

  if (isResolving) return <PageLoader label="Restoring your session" />;

  if (!isAuthenticated) {
    // `from` lets the login screen send the user back where they were aiming.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}

/**
 * Require a `(screen, operation)` grant.
 *
 * Renders an explanation rather than redirecting: bouncing someone to the
 * dashboard after they follow a shared link tells them nothing, whereas naming
 * the permission gives them something to ask an administrator for.
 *
 * @param {{screen:string, operation?:string, children:React.ReactNode}} props
 */
export function RequirePermission({ screen, operation = 'view', children }) {
  const { can, canOpen } = useAuth();

  const allowed = operation === 'view' ? canOpen(screen) : can(screen, operation);
  if (allowed) return children;

  return (
    <EmptyState
      icon="bi-shield-lock"
      title="You do not have access to this screen"
      message={`It needs the ${screen}:${operation} permission. An administrator can grant it from Users & Roles.`}
    />
  );
}

/**
 * The inverse: keep a signed-in user off the login screen.
 * Without it, hitting Back after signing in lands on a login form that then
 * bounces forward again, which reads as the app fighting the browser.
 */
export function RequireAnonymous({ children }) {
  const { isAuthenticated, isResolving, landingScreen } = useAuth();

  if (isResolving) return <PageLoader label="Checking your session" />;
  if (isAuthenticated) {
    const map = {
      'dashboard.admin': '/dashboard/admin',
      'dashboard.manager': '/dashboard/manager',
      'dashboard.rep': '/dashboard/rep',
      'dashboard.exec': '/dashboard/exec',
    };
    return <Navigate to={map[landingScreen] ?? '/'} replace />;
  }
  return children;
}
